import axios from 'axios';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Globe, LayoutDashboard, Image as ImageIcon, FileText, X, Pencil, Trash2 } from 'lucide-react';

const AA_CSS = `
  .aa-root { font-family:'Outfit',sans-serif; background:#f7f9fc; min-height:100vh; padding:2.5rem 1.25rem 5rem; }
  .aa-shell { max-width:920px; margin:0 auto; }

  .aa-topnav { display:flex; justify-content:center; gap:.75rem; margin-bottom:2.5rem; }
  .aa-topbtn { display:inline-flex; align-items:center; gap:.5rem; padding:.6rem 1.25rem; border-radius:2px; font-size:.72rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; border:1px solid rgba(4,52,99,.15); background:#ffffff; color:#043463; cursor:pointer; transition:all .2s; }
  .aa-topbtn:hover { border-color:#ee4c05; color:#ee4c05; transform:translateY(-1px); }
  .aa-topbtn.primary { background:#043463; color:#fff; border-color:#043463; }
  .aa-topbtn.primary:hover { background:#0e7fbb; border-color:#0e7fbb; color:#fff; }

  .aa-h1 { font-family:'Bebas Neue',sans-serif; font-size:clamp(1.9rem,4vw,2.5rem); letter-spacing:.04em; color:#043463; text-align:center; margin-bottom:2rem; }
  .aa-h1 span { color:#ee4c05; }

  .aa-card { background:#ffffff; border:1px solid rgba(4,52,99,.1); border-radius:2px; box-shadow:0 4px 20px rgba(4,52,99,.04); }
  .aa-form { padding:clamp(1.5rem,3vw,2.5rem); margin-bottom:3rem; }
  .aa-form-h { font-family:'Bebas Neue',sans-serif; font-size:1.375rem; letter-spacing:.04em; color:#043463; margin-bottom:1.5rem; display:flex; align-items:center; gap:.6rem; }
  .aa-form-h .dot { width:8px; height:8px; border-radius:100px; background:#ee4c05; }

  .aa-field { margin-bottom:1.25rem; }
  .aa-label { display:block; font-size:.65rem; font-weight:800; letter-spacing:.1em; text-transform:uppercase; color:#0e7fbb; margin-bottom:.5rem; }
  .aa-input { width:100%; padding:.75rem 1rem; border:1px solid rgba(4,52,99,.15); border-radius:2px; font-family:'Outfit',sans-serif; font-size:.9rem; color:#043463; background:#fff; transition:border-color .2s; }
  .aa-input:focus { outline:none; border-color:#ee4c05; }

  .aa-quill .ql-toolbar { border-radius:2px 2px 0 0; border-color:rgba(4,52,99,.15); }
  .aa-quill .ql-container { border-radius:0 0 2px 2px; border-color:rgba(4,52,99,.15); font-family:'Outfit',sans-serif; min-height:160px; }

  .aa-upload-row { display:grid; grid-template-columns:1fr 1fr; gap:1.25rem; }
  @media(max-width:600px){ .aa-upload-row { grid-template-columns:1fr; } }

  .aa-dropzone { border:1.5px dashed rgba(4,52,99,.25); border-radius:2px; padding:1.25rem; text-align:center; cursor:pointer; transition:border-color .2s, background .2s; position:relative; background:#fbfcfe; }
  .aa-dropzone:hover { border-color:#ee4c05; background:rgba(238,76,5,.03); }
  .aa-dropzone input { position:absolute; inset:0; opacity:0; cursor:pointer; }
  .aa-dropzone-icon { color:#0e7fbb; margin-bottom:.5rem; }
  .aa-dropzone-text { font-size:.78rem; color:#4a5568; }
  .aa-dropzone-text b { color:#043463; }

  .aa-preview-thumb { width:100%; aspect-ratio:16/9; object-fit:cover; border-radius:2px; display:block; background:#f0f4f8; }
  .aa-preview-wrap { position:relative; }
  .aa-preview-remove { position:absolute; top:.4rem; right:.4rem; width:26px; height:26px; border-radius:100px; background:rgba(4,52,99,.85); color:#fff; border:none; display:flex; align-items:center; justify-content:center; cursor:pointer; }
  .aa-preview-remove:hover { background:#ee4c05; }
  .aa-filename { font-size:.7rem; color:#4a5568; margin-top:.4rem; display:flex; align-items:center; gap:.35rem; }

  .aa-actions { display:flex; justify-content:flex-end; gap:.75rem; margin-top:1.75rem; }
  .aa-btn { padding:.7rem 1.5rem; border-radius:2px; font-size:.72rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; border:none; cursor:pointer; transition:all .2s; }
  .aa-btn-cancel { background:#eef1f5; color:#043463; }
  .aa-btn-cancel:hover { background:#e2e8f0; }
  .aa-btn-submit { background:#ee4c05; color:#fff; }
  .aa-btn-submit:hover { background:#043463; }
  .aa-btn-submit:disabled { opacity:.5; cursor:not-allowed; }

  .aa-list-h { font-family:'Bebas Neue',sans-serif; font-size:1.5rem; letter-spacing:.04em; color:#043463; text-align:center; margin-bottom:1.5rem; }

  .aa-grid { display:grid; grid-template-columns:1fr; gap:1.25rem; }
  @media(min-width:640px){ .aa-grid { grid-template-columns:repeat(2,1fr); } }

  .aa-item { overflow:hidden; transition:border-color .2s, transform .2s; }
  .aa-item:hover { border-color:rgba(238,76,5,.35); }
  .aa-item-img { width:100%; aspect-ratio:16/9; object-fit:cover; display:block; background:#f0f4f8; }
  .aa-item-img-ph { width:100%; aspect-ratio:16/9; background:linear-gradient(135deg,#f0f4f8,#e2e8f0); display:flex; align-items:center; justify-content:center; }
  .aa-item-body { padding:1.25rem; }
  .aa-item-title { font-family:'Bebas Neue',sans-serif; font-size:1.15rem; letter-spacing:.03em; color:#043463; line-height:1.2; margin-bottom:.9rem; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
  .aa-item-foot { display:flex; align-items:center; justify-content:space-between; padding-top:.75rem; border-top:1px solid rgba(4,52,99,.08); }
  .aa-item-tag { display:inline-flex; align-items:center; gap:.35rem; font-size:.62rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; color:#0e7fbb; }
  .aa-item-links { display:flex; gap:1rem; }
  .aa-item-links button { display:inline-flex; align-items:center; gap:.3rem; background:none; border:none; cursor:pointer; font-size:.72rem; font-weight:700; }
  .aa-edit-link { color:#0e7fbb; }
  .aa-edit-link:hover { color:#043463; }
  .aa-del-link { color:#ee4c05; }
  .aa-del-link:hover { color:#c73e00; }

  .aa-empty { text-align:center; padding:3.5rem 1rem; color:#4a5568; font-size:.875rem; }

  .aa-toast { position:fixed; top:1.5rem; left:50%; transform:translateX(-50%); z-index:60; padding:.8rem 1.5rem; border-radius:2px; font-size:.8rem; font-weight:600; color:#fff; box-shadow:0 10px 30px rgba(4,52,99,.2); }
  .aa-toast.success { background:#043463; }
  .aa-toast.error { background:#ee4c05; }

  .aa-modal-backdrop { position:fixed; inset:0; background:rgba(4,52,99,.55); display:flex; align-items:center; justify-content:center; z-index:70; padding:1rem; }
  .aa-modal { background:#fff; border-radius:2px; padding:2rem; max-width:360px; width:100%; text-align:center; }
  .aa-modal h4 { font-family:'Bebas Neue',sans-serif; font-size:1.375rem; letter-spacing:.03em; color:#ee4c05; margin-bottom:.75rem; }
  .aa-modal p { font-size:.85rem; color:#4a5568; margin-bottom:1.5rem; }
  .aa-modal-actions { display:flex; justify-content:center; gap:.75rem; }
`;

const ArticleAdmin = () => {
  const [articles, setArticles] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // blob URL or existing article.image URL
  const [fileInputKey, setFileInputKey] = useState(0); // forces file inputs to reset on edit/cancel
  const [editingId, setEditingId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const navigate = useNavigate();
  const apiUrl = 'https://imarikafoundation.org/api/api/api/articles/';
  // const apiUrl = 'http://127.0.0.1:8080/api/api/articles/';

  const fetchArticles = useCallback(() => {
    axios
      .get(apiUrl)
      .then((res) => setArticles(res.data.results ?? []))
      .catch((err) => {
        console.error('Error:', err);
        showFeedback('error', 'Failed to fetch articles.');
      });
  }, [apiUrl]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Clean up any blob preview URL we created, so we don't leak memory
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 3000);
  };

  const handleImageChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    setImage(selected);
    setImagePreview(URL.createObjectURL(selected));
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
    setFileInputKey((k) => k + 1); // remount the file input so it's visibly cleared
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', title);
    formData.append('content', content);
    if (file) formData.append('file', file);
    if (image) formData.append('image', image);

    setLoading(true);

    const request = editingId
      ? axios.put(`${apiUrl}${editingId}/`, formData)
      : axios.post(apiUrl, formData);

    request
      .then(() => {
        fetchArticles();
        resetForm();
        showFeedback('success', editingId ? 'Article updated!' : 'Article created!');
      })
      .catch((err) => {
        console.error('Error:', err);
        showFeedback('error', 'Failed to save article. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setFile(null);
    setImage(null);
    setImagePreview(null);
    setFileInputKey((k) => k + 1);
    setEditingId(null);
  };

  const handleEdit = (article) => {
    setTitle(article.title);
    setContent(article.content);
    setFile(null);
    setImage(null); // no new file picked yet — keep showing the existing image
    setImagePreview(article.image ?? null);
    setFileInputKey((k) => k + 1);
    setEditingId(article.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = () => {
    axios
      .delete(`${apiUrl}${deleteId}/`)
      .then(() => {
        fetchArticles();
        setShowConfirm(false);
        setDeleteId(null);
        showFeedback('success', 'Article deleted successfully!');
      })
      .catch((err) => {
        console.error('Error:', err);
        showFeedback('error', 'Failed to delete article.');
      });
  };

  return (
    <div className="aa-root">
      <style>{AA_CSS}</style>

      {feedback.message && (
        <div className={`aa-toast ${feedback.type}`}>{feedback.message}</div>
      )}

      <div className="aa-shell">
        <div className="aa-topnav">
          <button onClick={() => (window.location.href = '/')} className="aa-topbtn">
            <Globe size={16} /> Website
          </button>
          <button onClick={() => navigate('/admin')} className="aa-topbtn primary">
            <LayoutDashboard size={16} /> Admin Home
          </button>
        </div>

        <h1 className="aa-h1">Article <span>Management</span></h1>

        {/* Form */}
        <form onSubmit={handleSubmit} className="aa-card aa-form">
          <h3 className="aa-form-h">
            <span className="dot" />
            {editingId ? 'Edit Article' : 'Add New Article'}
          </h3>

          <div className="aa-field">
            <label className="aa-label">Title</label>
            <input
              type="text"
              placeholder="Article title"
              className="aa-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="aa-field">
            <label className="aa-label">Content</label>
            <div className="aa-quill">
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                placeholder="Write article content here..."
              />
            </div>
          </div>

          <div className="aa-field aa-upload-row">
            {/* Cover image */}
            <div>
              <label className="aa-label">Cover Image</label>
              {imagePreview ? (
                <div className="aa-preview-wrap">
                  <img src={imagePreview} alt="Cover preview" className="aa-preview-thumb" />
                  <button type="button" className="aa-preview-remove" onClick={handleRemoveImage} aria-label="Remove image">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="aa-dropzone">
                  <input
                    key={fileInputKey}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <ImageIcon size={22} className="aa-dropzone-icon" />
                  <div className="aa-dropzone-text"><b>Click to upload</b> a cover image</div>
                </div>
              )}
            </div>

            {/* PDF/doc attachment */}
            <div>
              <label className="aa-label">Attachment (optional)</label>
              <div className="aa-dropzone">
                <input
                  key={`file-${fileInputKey}`}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setFile(e.target.files[0] ?? null)}
                />
                <FileText size={22} className="aa-dropzone-icon" />
                <div className="aa-dropzone-text"><b>Click to upload</b> a PDF or Word doc</div>
              </div>
              {file && (
                <div className="aa-filename"><FileText size={12} /> {file.name}</div>
              )}
              {!file && editingId && (
                <div className="aa-filename">Leave empty to keep the current attachment.</div>
              )}
            </div>
          </div>

          <div className="aa-actions">
            {editingId && (
              <button type="button" onClick={resetForm} className="aa-btn aa-btn-cancel">
                Cancel
              </button>
            )}
            <button type="submit" className="aa-btn aa-btn-submit" disabled={loading}>
              {loading ? 'Saving…' : editingId ? 'Update Article' : 'Create Article'}
            </button>
          </div>
        </form>

        {/* List */}
        <h3 className="aa-list-h">All Articles</h3>

        {articles.length === 0 ? (
          <div className="aa-card aa-empty">No articles yet — create your first one above.</div>
        ) : (
          <div className="aa-grid">
            {articles.map((article) => (
              <div key={article.id} className="aa-card aa-item">
                {article.image ? (
                  <img src={article.image} alt="" className="aa-item-img" />
                ) : (
                  <div className="aa-item-img-ph">
                    <ImageIcon size={28} color="rgba(4,52,99,.15)" />
                  </div>
                )}
                <div className="aa-item-body">
                  <h4 className="aa-item-title">{article.title}</h4>
                  <div className="aa-item-foot">
                    <span className="aa-item-tag">
                      <FileText size={12} /> {article.file ? 'Has file' : 'Text only'}
                    </span>
                    <div className="aa-item-links">
                      <button onClick={() => handleEdit(article)} className="aa-edit-link">
                        <Pencil size={13} /> Edit
                      </button>
                      <button
                        onClick={() => {
                          setShowConfirm(true);
                          setDeleteId(article.id);
                        }}
                        className="aa-del-link"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showConfirm && (
        <div className="aa-modal-backdrop">
          <div className="aa-modal">
            <h4>Confirm Deletion</h4>
            <p>Are you sure you want to delete this article? This can't be undone.</p>
            <div className="aa-modal-actions">
              <button onClick={() => setShowConfirm(false)} className="aa-btn aa-btn-cancel">
                Cancel
              </button>
              <button onClick={handleDelete} className="aa-btn aa-btn-submit">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleAdmin;